import orderBy from "lodash/orderBy";

import getTaskTypes from "@fartherfinance/frontend/api/TaskWorkflows/requests/getTaskTypes";
import searchParameters from "@fartherfinance/frontend/api/TaskWorkflows/requests/searchParameters";
import { AdvisorRequestConfig } from "@fartherfinance/frontend/api/Types";

import { cache5Min } from "../../core/cache";
import {
  EnumOption,
  isResourceTypeWithValue,
  ResourceTypeResponse,
} from "../../core/types";

async function getAllTaskTypes(config: AdvisorRequestConfig) {
  return cache5Min("task-types", async () => {
    try {
      // Get available task type IDs from parameters search
      const searchRequest = {
        fields: {
          taskType: {
            type: "TaskType",
          },
        },
      };

      const searchResult = await searchParameters(searchRequest, {
        jwt: config.jwt,
        advisorId: config.advisorId,
      });

      if (!searchResult?.data?.taskType?.values) {
        console.warn("[getAllTaskTypes] No data in search parameters response");
        return [];
      }

      // Get full task type details from types endpoint
      const typesResult = await getTaskTypes({
        jwt: config.jwt,
        advisorId: config.advisorId,
      });

      if (!typesResult?.data) {
        console.warn("[getAllTaskTypes] No data in task types response");
        return [];
      }

      // Extract available task type IDs from search results
      const availableTypeIds = new Set<string>();

      searchResult.data.taskType.values.forEach((taskType) => {
        if (typeof taskType === "string") {
          availableTypeIds.add(taskType);
        } else if (
          taskType &&
          typeof taskType === "object" &&
          "value" in taskType
        ) {
          availableTypeIds.add(String(taskType.value));
        } else if (
          taskType &&
          typeof taskType === "object" &&
          "id" in taskType
        ) {
          availableTypeIds.add(String(taskType.id));
        }
      });

      // Filter task types by available IDs and map to proper format
      const filteredTaskTypes = typesResult.data
        .filter((taskType) => availableTypeIds.has(String(taskType.typeId)))
        .map((taskType) => ({
          typeId: taskType.typeId,
          name: taskType.name,
          displayName: taskType.displayName || taskType.name,
          cancelable: taskType.cancelable,
          isSystem: taskType.isSystem,
          subtypes: taskType.subtypes,
        }));

      return filteredTaskTypes;
    } catch (error) {
      console.error("[getAllTaskTypes] Error fetching task types:", error);
      return [];
    }
  });
}

export const fetchTaskTypes = async (
  requestConfig: AdvisorRequestConfig,
  values: ResourceTypeResponse[]
): Promise<EnumOption[]> => {
  const taskTypes = await getAllTaskTypes(requestConfig);

  // Create a map for quick lookup
  const taskTypeMap = new Map(
    taskTypes.map((taskType) => [String(taskType.typeId), taskType])
  );

  // Filter valid resources that have values
  const filteredResources = values.filter(isResourceTypeWithValue);

  // If we have valid resource values, preserve their order
  if (filteredResources.length > 0) {
    const result: EnumOption[] = [];

    for (const resource of filteredResources) {
      const taskType = taskTypeMap.get(String(resource.value));
      if (!taskType) {
        continue;
      }

      result.push({
        type: "TaskType",
        label: taskType.displayName || taskType.name,
        value: String(taskType.typeId),
      });
    }

    return result;
  }

  // If no specific values provided, return alphabetically sorted list (existing behavior)
  const taskTypeOptions = taskTypes.map((taskType) => ({
    type: "TaskType",
    label: taskType.displayName || taskType.name,
    value: String(taskType.typeId),
  }));

  return orderBy(taskTypeOptions, "label");
};
